import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './Coverflow.css';

const TOUCH = { move: false, lastX: 0, sign: 0, lastMove: 0 };

const TRANSITIONS = [
  'transitionend', 'oTransitionEnd', 'otransitionend', 'MSTransitionEnd', 'webkitTransitionEnd',
];

const CURRENT_FIGURE_SCALE = 1;
const PAD_RIGHT_LEFT = 190;
const SPACE_QUANTITY = 5;

function Coverflow({
  children, enableScroll, clickable, active, media, classes, className,
  width: propWidth, height: propHeight,
}) {
  const refNode = useRef(null);
  const stageRef = useRef(null);
  const figureRefs = useRef([]);

  const [state, setState] = useState({
    current: 0, move: 0, width: propWidth, height: propHeight, baseWidth: 0, firstLoad: true,
  });

  const length = React.Children.count(children);
  const center = ~~(length / 2);

  const removePointerEvents = useCallback(() => {
    if (stageRef.current) stageRef.current.style.pointerEvents = 'auto';
  }, []);

  const updateDimensions = useCallback((activeArg, forceHeight = null) => {
    const node = refNode.current;
    const width = node ? node.offsetWidth : 0;
    const height = forceHeight !== null ? forceHeight : (node ? node.offsetHeight : 0);
    const baseWidth = (width - PAD_RIGHT_LEFT * 2) / SPACE_QUANTITY;

    setState(prev => {
      const next = { ...prev, width, height, baseWidth };
      if (typeof activeArg === 'number' && ~~activeArg < length) {
        const activeImg = ~~activeArg;
        next.current = activeArg;
        next.move = baseWidth * (center - activeImg);
      }
      return next;
    });
  }, [length, center]);

  // garder la dernière version pour le listener resize attaché une seule fois
  const updateDimensionsRef = useRef(updateDimensions);
  updateDimensionsRef.current = updateDimensions;

  // componentDidMount : dimensions initiales + listeners transitionend + resize
  useEffect(() => {
    updateDimensionsRef.current(active);

    const onResize = () => updateDimensionsRef.current();
    window.addEventListener('resize', onResize);

    const nodes = figureRefs.current.slice(0, length);
    TRANSITIONS.forEach(event => {
      nodes.forEach(node => node && node.addEventListener(event, removePointerEvents));
    });

    return () => {
      window.removeEventListener('resize', onResize);
      TRANSITIONS.forEach(event => {
        nodes.forEach(node => node && node.removeEventListener(event, removePointerEvents));
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length]);

  // componentDidUpdate : changement de active ou height (prop)
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    setState(prev => (prev.firstLoad ? { ...prev, firstLoad: false } : prev));
    updateDimensions(active, propHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, propHeight]);

  const handleFigureStyle = (index, current) => {
    const { width, baseWidth, move } = state;
    const style = {};
    const offset = length % 2 === 0 ? -width / 10 : 0;
    const rotate = 0;
    style.width = `${baseWidth}px`;
    let overlay = 0;

    if (index === current) {
      style.width = `${baseWidth * 3}px`;
      style.transform = `translateX(${move + offset}px) scale(${CURRENT_FIGURE_SCALE}`;
      style.zIndex = length;
      style.opacity = 1;
    } else if (index < current) {
      const leftDepth = current - index;
      let pourcentDepth = 1 - leftDepth / 10;
      pourcentDepth = pourcentDepth < 0.7 ? 0 : pourcentDepth;
      const offsetCustom = baseWidth * 0.25 * leftDepth + (baseWidth - baseWidth * pourcentDepth) * leftDepth;
      style.transform = `translateX(${move + offset + offsetCustom}px) rotateY(${rotate}deg) scale(${pourcentDepth}`;
      style.zIndex = length - leftDepth;
      style.opacity = pourcentDepth;
    } else {
      const rightDepth = index - current;
      let pourcentDepth = 1 - rightDepth / 10;
      pourcentDepth = pourcentDepth < 0.7 ? 0 : pourcentDepth;
      const offsetCustom = baseWidth * 0.25 * rightDepth + (baseWidth - baseWidth * pourcentDepth) * rightDepth;
      style.transform = ` translateX(${move + offset - offsetCustom}px) rotateY(${rotate}deg) scale(${pourcentDepth})`;
      style.zIndex = length - rightDepth;
      style.opacity = pourcentDepth;
    }
    overlay = style.opacity === 0.9 ? 0.25 : style.opacity === 0.8 ? 0.5 : style.opacity === 0.7 ? 0.75 : 0;
    return { style, overlay };
  };

  const handleFigureClick = (index, action, e) => {
    if (!clickable) { e.preventDefault(); return; }
    if (state.current === index) {
      if (typeof action === 'string') window.open(action, '_blank');
      removePointerEvents();
    } else {
      e.preventDefault();
      const move = (center - index) * state.baseWidth;
      setState(prev => ({ ...prev, current: index, move }));
    }
  };

  const hasPrevFigure = () => state.current - 1 >= 0;
  const hasNextFigure = () => state.current + 1 < children.length;

  const handlePrevFigure = () => {
    const { baseWidth, current } = state;
    const distance = center - (current - 1 < 0 ? children.length - 1 : current - 1);
    const move = distance * baseWidth;
    if (current - 1 >= 0) {
      setState(prev => ({ ...prev, current: current - 1, move }));
      TOUCH.lastMove = move;
    }
  };

  const handleNextFigure = () => {
    const { baseWidth, current } = state;
    const distance = center - (current + 1 >= children.length ? 0 : current + 1);
    const move = distance * baseWidth;
    if (current + 1 < children.length) {
      setState(prev => ({ ...prev, current: current + 1, move }));
      TOUCH.lastMove = move;
    }
  };

  const handleWheel = (e) => {
    const delta = Math.abs(e.deltaY) === 125 ? e.deltaY * -120 : e.deltaY < 0 ? -600000 : 600000;
    const count = Math.ceil(Math.abs(delta) / 120);
    if (count > 0) {
      const sign = Math.abs(delta) / delta;
      if (sign > 0 && hasPrevFigure()) { e.preventDefault(); handlePrevFigure(); }
      else if (sign < 0 && hasNextFigure()) { e.preventDefault(); handleNextFigure(); }
    }
  };

  const handleTouchStart = (e) => {
    TOUCH.lastX = e.nativeEvent.touches[0].clientX;
    TOUCH.lastMove = state.move;
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const { baseWidth } = state;
    const clientX = e.nativeEvent.touches[0].clientX;
    const move = clientX - TOUCH.lastX;
    const totalMove = TOUCH.lastMove - move;
    const sign = Math.abs(move) / move;
    if (Math.abs(totalMove) >= baseWidth) {
      if (sign > 0) handlePrevFigure();
      else if (sign < 0) handleNextFigure();
    }
  };

  const renderFigureNodes = () => {
    const { current, baseWidth, firstLoad } = state;
    return React.Children.map(children, (child, index) => {
      const figureElement = React.cloneElement(child, {
        id: `block${index}`,
        className: `block ${index === current && `active`} flexRow`,
        basewidth: Math.round(baseWidth),
      });
      const { style, overlay } = handleFigureStyle(index, current);
      const isFast = current - index > 2 || current - index < -2;
      return (
        <figure
          className={`figure ${!firstLoad && `animate`} ${!firstLoad && isFast && `fast`} `}
          key={index}
          onClick={e => handleFigureClick(index, figureElement.props['data-action'], e)}
          style={style}
          ref={el => { figureRefs.current[index] = el; }}
        >
          {figureElement}
          {overlay > 0 && (
            <div className={`oo-overlay`} style={{ backgroundColor: `rgba(23, 23, 25, ${overlay})` }} />
          )}
        </figure>
      );
    });
  };

  const { width, height } = state;
  return (
    <div ref={refNode}>
      <div
        className={`${className} container`}
        style={Object.keys(media).length !== 0 ? media : { ...classes, width: `${width}px`, height: `${height}px` }}
        onWheel={enableScroll ? handleWheel : null}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        tabIndex="-1"
      >
        <div className={`coverflow`}>
          <div className={`preloader`} />
          <div className={`stage`} ref={stageRef}>
            {renderFigureNodes()}
          </div>
        </div>
      </div>
    </div>
  );
}

Coverflow.propTypes = {
  children: PropTypes.node.isRequired,
  enableScroll: PropTypes.bool,
  clickable: PropTypes.bool,
  active: PropTypes.number,
  media: PropTypes.any,
  classes: PropTypes.object,
  className: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Coverflow.defaultProps = {
  enableScroll: true,
  clickable: true,
  classes: {},
  className: '',
  active: 0,
  media: {},
  width: 'auto',
  height: 'auto',
};

Coverflow.displayName = 'Coverflow';

export default Coverflow;
